// Tiny presentational helpers: numbers stay numbers until here (24.14).
import type { Season } from '../sim/api';

export function whole(x: number): string {
  const r = Math.round(x);
  return (r === 0 ? 0 : r).toString();
}

export function oneDecimal(x: number): string {
  const r = Math.round(x * 10) / 10;
  return (r === 0 ? 0 : r).toFixed(1);
}

export function signed(x: number): string {
  const r = Math.round(x * 10) / 10;
  return r > 0 ? `+${r.toFixed(1)}` : r === 0 ? '0.0' : r.toFixed(1);
}

export function signedWhole(x: number): string {
  const r = Math.round(x);
  return r > 0 ? `+${r}` : r === 0 ? '0' : `${r}`;
}

export function percent(x: number): string {
  return `${Math.round(x * 100)}%`;
}

export function times(x: number): string {
  return `${(Math.round(x * 100) / 100).toFixed(2)}x`;
}

export type NumFormat = 'whole' | 'one' | 'signed' | 'signedWhole' | 'percent' | 'times';

export function formatNum(x: number, format: NumFormat): string {
  switch (format) {
    case 'whole':
      return whole(x);
    case 'one':
      return oneDecimal(x);
    case 'signed':
      return signed(x);
    case 'signedWhole':
      return signedWhole(x);
    case 'percent':
      return percent(x);
    case 'times':
      return times(x);
  }
}

/** 'harsh-winter' -> 'Harsh winter' (for ids without a label in the text table). */
export function humanize(id: string): string {
  const spaced = id.replace(/[-_]/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

const SEASON: Record<Season, string> = {
  spring: 'Spring',
  summer: 'Summer',
  autumn: 'Autumn',
  winter: 'Winter',
};

export function seasonName(season: Season): string {
  return SEASON[season];
}

/**
 * Fill `{key}` placeholders from params; `{key|one|many}` picks the singular or the plural word by
 * the number in `key` (as the Ledger's sketch templates do).
 */
export function template(text: string, params: Readonly<Record<string, string | number>>): string {
  return text.replace(
    /\{(\w+)(?:\|([^|}]*)\|([^}]*))?\}/g,
    (_m, key: string, one: string | undefined, many: string | undefined) => {
      const value = params[key];
      if (one !== undefined && many !== undefined) return Number(value) === 1 ? one : many;
      return value === undefined ? '' : String(value);
    },
  );
}
